import { literal, object, string, union, z } from 'zod';

import { serviceNowIdentifierSchema } from '../common/matchers';
import type { ApiEnvironmentDto } from './ApiEnvironmentDto';
import { AccessTypes } from './AppDto';
import type { OrganizationDto } from './OrganizationDto';
import type { ScopeDto } from './ScopeDto';
import type { ScopeRequestDto } from './ScopeRequestDto';
import type { TeamDto } from './TeamDto';

export type ApiStage = 'launched' | 'upcoming' | 'deprecated' | 'decommissioned';

export type ApiClassification = 'system' | 'domain' | 'experience' | 'technical' | 'public' | 'partner' | 'legacy';

export enum SeverityClassification {
  OPEN = 'open',
  INTERNAL = 'internal',
  CONFIDENTIAL = 'confidential',
  STRICTLY_CONFIDENTIAL = 'strictly-confidential',
}

export interface ApiInfoDto {
  id: string;
  slug: string;
  ordering: number;
  description: string | null;
  availableInLiveMode: boolean;
  attachable: boolean;
  name: string;
  icon: string;
  stage: ApiStage;
  approvalStage: 'test' | 'live' | 'public';
  documentationAnchor: string | null;
  restricted: boolean;
  productPageUrl: string | null;
  enableStatus: boolean;
  isPublic: boolean;
  classification: ApiClassification;
  tags: string[];
  termsOfUse: string | null;
  termsOfUseUpdatedAt: number | null;
  accessTypes?: Array<'API_KEYS' | 'O_AUTH_SYSTEM_TO_SYSTEM'> | null;
  serviceNowIdentifier: string | null;
  serviceNowServiceDomainId: string | null;
  enableServiceNowRequest: boolean | null;
  piiExposed: boolean | null;
  gdprCompliant: boolean | null;
  severityClassification: SeverityClassification | null;
}

export enum ApiType {
  REST = 'REST',
  EVENT = 'EVENT',
}

export interface ApiDto {
  id: string;
  slug: string;
  ordering: number;
  description: string | null;
  availableInLiveMode: boolean;
  attachable: boolean;
  name: string;
  icon: string;
  stage: ApiStage;
  approvalStage: 'test' | 'live' | 'public';
  documentationAnchor: string | null;
  restricted: boolean;
  productPageUrl: string | null;
  enableStatus: boolean;
  isPublic: boolean;
  classification: ApiClassification;
  tags: string[];
  environments: ApiEnvironmentDto[];
  termsOfUse: string | null;
  termsOfUseUpdatedAt: number | null;
  organization: OrganizationDto | null;
  teams: TeamDto[];
  currentVersion: string | null;
  upcomingVersion: string | null;
  scopes?: ScopeDto[];
  accessTypes?: AccessTypes[] | null;
  serviceNowIdentifier: string | null;
  serviceNowServiceDomainId: string | null;
  enableServiceNowRequest: boolean;
  enableFaq: boolean;
  piiExposed: boolean | null;
  gdprCompliant: boolean | null;
  severityClassification: SeverityClassification | null;
  isFavorite?: boolean;
  isOwner?: boolean;
  appId: string | null;
  ownerTeamId: string | null;
  type: ApiType;
}

export interface ApiWithStatusDto
  extends Omit<
    ApiDto,
    'environments' | 'organization' | 'teams' | 'currentVersion' | 'upcomingVersion' | 'scopes' | 'ownerTeamId'
  > {
  grantStatus: string | null;
  useCase: string | null;
  attached: boolean;
}

export interface ApiWithStatusAndScopesDto
  extends Omit<
    ApiDto,
    'environments' | 'organization' | 'teams' | 'currentVersion' | 'upcomingVersion' | 'ownerTeamId'
  > {
  grantStatus: string | null;
  useCase: string | null;
  attached: boolean;
  scopeRequests: ScopeRequestDto[];
}

export const uploadPublicKeyInputSchema = object({
  mode: union([literal('sandbox'), literal('production')]),
  publicKey: string().min(1),
});

export type UploadPublicKeyInputDto = z.infer<typeof uploadPublicKeyInputSchema>;

export interface ApiPlainDto {
  id: string;
  slug: string;
  ordering: number;
  description: string | null;
  availableInLiveMode: boolean;
  attachable: boolean;
  name: string;
  icon: string;
  stage: ApiStage;
  approvalStage: 'test' | 'live' | 'public';
  documentationAnchor: string | null;
  productPageUrl: string | null;
  enableStatus: boolean;
  isPublic: boolean;
  classification: 'system' | 'domain' | 'experience' | 'technical' | 'public' | 'partner' | 'legacy';
  tags: string[];
  accessTypes?: Array<'API_KEYS' | 'O_AUTH_SYSTEM_TO_SYSTEM'> | null;
  serviceNowIdentifier: string | null;
  enableServiceNowRequest: boolean;
}

export const OAuthIntegrationInfoSchema = z.object({
  serviceNowIdentifier: serviceNowIdentifierSchema,
  serviceNowServiceDomainId: string().uuid().nullable(),
  severityClassification: string().and(
    z.enum([
      SeverityClassification.CONFIDENTIAL,
      SeverityClassification.INTERNAL,
      SeverityClassification.OPEN,
      SeverityClassification.STRICTLY_CONFIDENTIAL,
    ]),
  ),
  piiExposed: z.boolean().default(false),
  gdprCompliant: z.boolean().default(false),
});

export type ServiceNowInfoInputDto = z.infer<typeof OAuthIntegrationInfoSchema>;

// UI form validation schema that includes classification for conditional validation
export const OAuthIntegrationInfoWithClassificationSchema = OAuthIntegrationInfoSchema.extend({
  classification: z.enum(['system', 'domain', 'experience', 'technical', 'public', 'partner', 'legacy']),
}).refine((data) => (data.classification === 'domain' ? data.serviceNowServiceDomainId !== null : true), {
  message: 'Service Domain is required when classification is "domain"',
  path: ['serviceNowServiceDomainId'],
});

export type ServiceNowInfoWithClassificationInputDto = z.infer<typeof OAuthIntegrationInfoWithClassificationSchema>;

export const upsertApiFavouriteInputSchema = object({
  isFavourite: z.boolean(),
});

export type upsertApiFavouriteInputDto = z.infer<typeof upsertApiFavouriteInputSchema>;
